import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import Micromodal from 'micromodal';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getModalOption, Setting } from '../../App';
import { billingPolicyIntervals, CUSTOMER_API_URL } from '../../constants';
import base from '../../styles/Base.module.css';
import style from '../../styles/OtherMenu.module.css';

// import ChangeDeliverDate from './ChangeDeliverDate';

import { useSubscriptionDetailListItem } from '../../hooks/use-subscription-detail-list-item';

type Plan = {
  planId: string;
  name: string;
  description: string;
  billingPolicyInterval: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  billingPolicyIntervalCount: number;
  pricingPolicyAdjustmentType: string | null;
  pricingPolicyAdjustmentValue: number | null;
};

type Box = {
  boxId: string;
  title: string;
  description: string;
  parentProductId: string;
};

type PlanGroup = {
  merchantCode: string;
  name: string;
  planGroupId: string;
  plans: Plan[];
  boxes: Box[];
};

const OtherMenu: FC<{
  setting: Setting;
  subscriptionContractId: string;
  updateSubscription: () => void;
  last: boolean;
}> = ({ setting, subscriptionContractId, updateSubscription, last }) => {
  // console.log('subscription', subscription);
  const {
    subscription,
    setSubscription,
    deliveryDate,
    paymentMethodUpdate,
    couponCode,
    setCouponCode,
    // requestApplyCouponCode,
    onChangeCouponCode,
    onClickCouponCode,
    applyCouponCode,
    couponSubscriptionId,
    setCouponSubscriptionId,
    skip,
    skipCancel,
    skipCount,
    setSkipCount,
    planGroups,
  } = useSubscriptionDetailListItem(setting, subscriptionContractId);

  const [dispOtherMenu, setDispOtherMenu] = useState<boolean>(false);
  const [otherMenuId, setOtherMenuId] = useState<string>('');
  const [isProc, setIsProc] = useState<boolean>(false);
  const [isSkip, setIsSkip] = useState<boolean>(false);

  // スクロール量を親windowから受け取る
  const [scrollOffset, setScrollOffset] = useState(0);
  useEffect(() => {
    const parentScrollEvent = (event: MessageEvent) => {
      const { pageYOffset } = event.data;
      if (!pageYOffset) return;
      setScrollOffset(pageYOffset + 120);
    };
    window.addEventListener('message', parentScrollEvent);
    return () => window.removeEventListener('message', parentScrollEvent);
  }, []);

  // コンポーネントがマウントされたときにイベントリスナーを追加
  useEffect(() => {
    window.addEventListener('keydown', handleEscapeKey);
    document.body.addEventListener('click', handleBodyClick);

    Micromodal.init();

    // コンポーネントがアンマウントされたときにイベントリスナーを削除
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
      document.body.removeEventListener('click', handleBodyClick);
    };
  }, []);

  // bodyがクリックされたときの処理
  const handleBodyClick = (event: any) => {
    const closestElement = event.target.closest('[data-otherMenuBox]');
    const closestElementBtn = event.target.closest('button');

    if (closestElement === null && closestElementBtn !== null) {
      setDispOtherMenu(true);
    } else {
      setDispOtherMenu(false);
      setOtherMenuId('');
    }
  };

  // ESCキーが押されたときの処理
  const handleEscapeKey = (event: any) => {
    if (event.key === 'Escape') {
      setDispOtherMenu(false);
      setOtherMenuId('');
    }
  };

  const lastEventData = useRef(null);

  useEffect(() => {
    window.removeEventListener('message', skipNextDelivery);
    if (subscription && planGroups && deliveryDate) {
      const canceledAtNullCount = subscription.subscriptionHistories.filter((item) => item.canceledAt === null).length;

      if (canceledAtNullCount > 0) {
        setIsSkip(true);
      } else {
        setIsSkip(false);
      }
      window.addEventListener('message', skipNextDelivery);
    }
  }, [subscription, planGroups, isProc, isSkip]);

  const skipNextDelivery = useCallback(
    async (e: MessageEvent<any>) => {
      if (lastEventData.current && JSON.stringify(lastEventData.current) === JSON.stringify(e.data)) {
        // データが同じであれば処理をスキップ
        return;
      }

      // 変更を保存ボタンが押されたとき(プランのスキップ)
      if (e.data.action && e.data.action === 'skipPlan') {
        if (isProc) {
          return;
        }

        if (!subscription) return;

        if (subscription.subscriptionContractId === e.data.id) {
          setIsProc(true);

          setSubscription({ ...subscription!, disabled: true });
          axios
            .post(
              `${CUSTOMER_API_URL}?action=skipSubscription`,
              {
                subscriptionId: subscription.subscriptionContractId,
                skipCount,
                skipDate: deliveryDate ? deliveryDate.current.format('YYYY年M月D日') : '',
              },
              {
                headers: { Authorization: setting.token },
              },
            )
            .then((response) => {
              const nextBilling = new Date(response.data.nextBillingDate);

              setSubscription({
                ...subscription!,
                nextBillingDate: nextBilling,
                disabled: false,
                subscriptionHistories: response.data.subscriptionHistories,
              });

              updateSubscription();
              // Parentへ送信
              window.parent.postMessage(
                {
                  action: 'showSkipCompleteModal',
                  subscriptionId: subscription.subscriptionContractId,
                },
                '*',
              );
            })
            .catch((error: AxiosError) => {
              console.log('error:', error);
            });
        }
      }

      // 変更を保存ボタンが押されたとき(プランのスキップ)
      if (e.data.action && e.data.action === 'skipCancellation') {
        if (isProc) {
          return;
        }

        if (!subscription) return;

        if (subscription.subscriptionContractId === e.data.id) {
          setIsProc(true);

          setSubscription({ ...subscription!, disabled: true });

          axios
            .post(
              `${CUSTOMER_API_URL}?action=skipCancelSubscription`,
              {
                subscriptionId: subscription.subscriptionContractId,
                skipCancelDate: deliveryDate ? deliveryDate.current.format('YYYY年M月D日') : '',
              },
              {
                headers: { Authorization: setting.token },
              },
            )
            .then((response) => {
              const nextBilling = new Date(response.data.nextBillingDate);
              setSubscription({
                ...subscription!,
                nextBillingDate: nextBilling,
                disabled: false,
                subscriptionHistories: response.data.subscriptionHistories,
              });

              updateSubscription();
              // Parentへ送信
              window.parent.postMessage(
                {
                  action: 'showSkipCancellationCompleteModal',
                  subscriptionId: subscription.subscriptionContractId,
                },
                '*',
              );
            })
            .catch((error: AxiosError) => {
              if (error.response!.status >= 500) {
                throw error;
              }
            });

          Micromodal.close('modal-skip-cancel');
        }
      }

      // 変更を保存ボタンが押されたとき(クーポンの適用)
      if (e.data.action && e.data.action === 'applyCouponCode') {
        console.log('applyCouponCode', e.data);

        if (isProc) {
          return;
        }
        if (!subscription) return;

        if (subscription.subscriptionContractId === e.data.id) {
          setIsProc(true);

          const couponCode = e.data.couponCode;

          try {
            await axios.post(
              `${CUSTOMER_API_URL}?action=updateSubscriptionCouponHistory`,
              {
                subscriptionContractId: e.data.id,
                couponCode: couponCode,
              },
              {
                headers: { Authorization: setting.token },
              },
            );
          } catch (error) {
            console.log({ error });
            let errorCode = 'unknown';
            let errorMessages = undefined;
            if (typeof error === 'object' && error !== null) {
              // Try to access response and status if available
              errorCode = (error as any).response?.status || (error as any).status || 'unknown';
              errorMessages = (error as any).response?.data;
            }
            // Parentへ送信
            window.parent.postMessage(
              {
                action: 'showErrorModal',
                subscriptionId: subscription.subscriptionContractId,
                errorCode: errorCode,
                errorMessages: errorMessages,
                errorType: 'couponApply',
              },
              '*',
            );
            setIsProc(false); // これを追加
            return;
          }

          // 1秒おいてからParentへ送信
          setTimeout(() => {
            // Parentへ送信
            window.parent.postMessage(
              {
                action: 'showCouponApplyCompleteModal',
                subscriptionId: subscription.subscriptionContractId,
              },
              '*',
            );
            setIsProc(false); // これを追加
          }, 1000);
        }
      }

      // 変更を保存ボタンが押されたとき(支払い方法の適用)
      if (e.data.action && e.data.action === 'sendChangePayment') {
        if (isProc) {
          return;
        }
        if (!subscription) return;

        if (subscription.subscriptionContractId === e.data.id) {
          setIsProc(true);

          axios
            .post(
              `${CUSTOMER_API_URL}?action=paymentMethodUpdate`,
              {
                subscriptionId: e.data.id,
              },
              {
                headers: { Authorization: setting.token },
              },
            )
            .then((response) => {
              // Micromodal.close(`modal-payment-change-${modalid}`);
              // Micromodal.show(`modal-payment-change-complete-${modalid}`);
            })
            .catch((error) => {
              // Micromodal.close('modal-payment-change');
              // Micromodal.show('modal-error', getModalOption(setting));
            });
        }
      }

      lastEventData.current = e.data;
    },
    [subscription, planGroups],
  );

  if (!subscription) {
    return <></>;
  }

  let mindate = deliveryDate ? dayjs(deliveryDate!.min.toDate()) : undefined;
  let maxdate = deliveryDate && deliveryDate!.max ? dayjs(deliveryDate!.max.toDate()) : undefined;
  let betweenDate = maxdate && mindate ? maxdate.diff(mindate, 'days') : undefined;
  let selectdate = deliveryDate && deliveryDate!.change ? dayjs(deliveryDate!.change.toDate()) : undefined;
  // mindate から betweenDate までの配列を作成
  let betweenDateArray = [];
  if (betweenDate) {
    for (let i = 0; i < betweenDate; i++) {
      betweenDateArray.push(mindate!.add(i, 'day'));
    }
  }

  // その他メニューの表示 / 非表示
  // モーダルID
  const modalid = subscriptionContractId.replace(/gid:\/\/shopify\/SubscriptionContract\//g, '');

  // その他メニューの表示 / 非表示 切り替え
  // ================================================
  const onClickOtherMenuBtn = (subscriptionContractId: any) => {
    setOtherMenuId(subscriptionContractId);
    setDispOtherMenu(!dispOtherMenu);
    // if(!dispOtherMenu){
    //   setOtherMenuId('');
    // }
  };

  // クーポンコード適用モーダルを表示
  //  ================================================
  const onClickShowCouponModal = () => {
    // Parentへ送信
    window.parent.postMessage(
      {
        action: 'showCouponModal',
        subscriptionId: subscriptionContractId,
      },
      '*',
    );

    // onClickCouponCode(subscriptionContractId);
    setDispOtherMenu(false);
    setOtherMenuId('');
  };

  // 解約モーダルを表示
  //  ================================================
  const onClickShowCancellationModal = () => {
    Micromodal.show(`modal-cancellation-${modalid}`);
    setDispOtherMenu(false);
    setOtherMenuId('');
  };

  // お支払い方法変更モーダルを表示
  //  ================================================
  const openPaymentChange = () => {
    // Parentへ送信
    window.parent.postMessage(
      {
        action: 'showChangePaymentModal',
        subscriptionId: subscriptionContractId,
      },
      '*',
    );
    // Micromodal.show(`modal-payment-change-${modalid}`, getModalOption(setting));
    setDispOtherMenu(false);
    setOtherMenuId('');
  };

  // お届け日変更モーダルを表示
  //  ================================================
  const openChangeDeliveryDate = () => {
    Micromodal.show(`modal-cancellation-${modalid}`, getModalOption(setting));
    setOtherMenuId('');
    setDispOtherMenu(false);

    // iframe枠外モーダル表示
    // -------------------------------------------
    let betweenDateArray = [];
    if (betweenDate) {
      for (let i = 0; i < betweenDate; i++) {
        betweenDateArray.push(mindate!.add(i, 'day'));
      }
    }

    // お届け予定日取得
    const betweenDates = betweenDateArray.map((v) => {
      let selected = v.format('YYYY-MM-DD') === selectdate?.format('YYYY-MM-DD') ? true : false;
      return {
        label: v.format('YYYY/MM/DD'),
        value: v.format('YYYY-MM-DD'),
        selected: selected,
      };
    });

    // お届け時間帯取得
    let deliveryTime = subscription.shop.deliveryTimes.map((deliveryTime) => {
      let selected = deliveryTime.tag === deliveryDate?.time ? true : false;
      return {
        label: deliveryTime.title,
        value: deliveryTime.tag,
        selected: selected,
      };
    });

    const getPlanModifyList = () => {
      if (!planGroups || planGroups.length <= 0 || !subscription) {
        return;
      }

      // 設定されている商品バリエーションを確認
      const planGroupsFilter = planGroups
        .filter((planGroup: PlanGroup) => {
          // 同一プランが含まれるか確認
          const plan = planGroup!.plans.find((plan) => {
            return (
              plan.billingPolicyInterval === subscription.billingPolicyInterval &&
              plan.billingPolicyIntervalCount === subscription.billingPolicyIntervalCount
            );
          });
          if (!plan) {
            return false;
          }

          // SubscriptionLineのsellingPlanIdがPlanGroupのPlanのいずれかに一致する
          return subscription.subscriptionLines.some((line) =>
            planGroup.plans.some((plan) => plan.planId === line.sellingPlanId),
          );
        })
        .map((planGroup: PlanGroup) => {
          return planGroup.plans;
        });

      let samePlans: Plan[] = [];
      if (planGroupsFilter.length <= 1) {
        samePlans = planGroupsFilter.flat();
      } else {
        // 全てのプランで、重複しているプランを抽出
        let plans = planGroupsFilter.flat();
        while (plans.length) {
          const target = plans.shift();
          const check = plans.filter((plan) => {
            return (
              plan.billingPolicyIntervalCount === target!.billingPolicyIntervalCount &&
              plan.billingPolicyInterval === target!.billingPolicyInterval
            );
          });

          if (check.length) {
            samePlans = samePlans.concat([target!]);
            plans = plans.filter(
              (plan) =>
                plan.billingPolicyIntervalCount !== target!.billingPolicyIntervalCount ||
                plan.billingPolicyInterval !== target!.billingPolicyInterval,
            );
          }
        }
      }

      return samePlans;
    };

    const modifyList = getPlanModifyList();
    let is_BOXPlan = false;

    const sellingPlanId = subscription && subscription.subscriptionLines[0].sellingPlanId;
    const matchedPlan = modifyList && modifyList.find((plan) => plan.planId === sellingPlanId);

    if (matchedPlan) {
      is_BOXPlan = /BOX/.test(matchedPlan.name);
    }

    // お届けサイクル取得
    const deliveryInterval = modifyList!.map((plan) => {
      let selected: boolean = plan.planId === subscription.subscriptionLines[0].sellingPlanId ? true : false;
      let is_boxplan = plan.name.match(/BOX/) ? true : false;

      // BOXプランの場合は[BOX]を表示
      if ((is_BOXPlan && is_boxplan) || (!is_BOXPlan && !is_boxplan)) {
        return {
          label: plan.billingPolicyIntervalCount + billingPolicyIntervals[plan.billingPolicyInterval],
          value: plan.planId,
          selected: selected,
        };
      } else {
        return null;
      }
    });

    // Parentへ送信
    window.parent.postMessage(
      {
        action: 'showDeliverModal',
        betweenDateArray: betweenDates,
        deliveryTime: deliveryTime,
        deliveryInterval: deliveryInterval,
        subscriptionId: subscription.subscriptionContractId,
      },
      '*',
    );

    setDispOtherMenu(false);
    setOtherMenuId('');
  };

  // お届けをスキップモーダルを表示
  //  ================================================
  const openSkip = () => {
    // Parentへ送信
    window.parent.postMessage(
      {
        action: 'showSkipModal',
        subscriptionId: subscription.subscriptionContractId,
      },
      '*',
    );

    // Micromodal.show(`modal-skip-${modalid}`);
    setDispOtherMenu(false);
    setOtherMenuId('');
  };

  const openSkipCancel = () => {
    openSkip();
    Micromodal.close(`modal-cancellation-${modalid}`);
  };

  const openSkipCancellation = () => {
    window.parent.postMessage(
      {
        action: 'showSkipCancellationModal',
        subscriptionId: subscription.subscriptionContractId,
      },
      '*',
    );

    setDispOtherMenu(false);
    setOtherMenuId('');
  };

  const onSkipComplete = () => {
    updateSubscription();
    Micromodal.close(`modal-skip-complete-${modalid}`);
    // window.location.reload();
  };

  // 同じ商品を追加注文
  //  ================================================
  const onClickAddSameProduct = () => {
    // 現在と同じ商品をカートに追加する
    const lineItemsToAdd: any[] = [];
    subscription.subscriptionLines.forEach((line) => {
      lineItemsToAdd.push({
        variantId: line.variantId,
        quantity: line.quantity,
      });

      window.parent.postMessage(
        {
          action: 'addLineItems',
          lineItemsToAdd,
        },
        '*',
      );
    });
  };

  // 表示最後の場合はクラスを付与
  //  ================================================
  const lastClass = last ? style.OtherMenuLast : '';

  return (
    <>
      <div className={`${style.OtherMenu} ${lastClass}`}>
        <button
          className={style.OtherMenuBtn}
          onClick={() => {
            onClickOtherMenuBtn(subscription.subscriptionContractId);
          }}
        >
          <img src="/image/button_other.svg" alt="その他" />
        </button>
        {dispOtherMenu && otherMenuId === subscription.subscriptionContractId && (
          <div className={style.OtherMenuBox} data-otherMenuBox>
            <p className={style.OtherMenuTitle}>その他</p>
            <ul className={style.OtherMenuList}>
              <li>
                <button type="button" className={style.OtherMenuListBtn} onClick={onClickAddSameProduct}>
                  同じ商品を追加注文
                </button>
              </li>
              <li>
                <button type="button" className={style.OtherMenuListBtn} onClick={openSkip}>
                  次回お届けをスキップ
                </button>
              </li>
              {isSkip ? (
                <li>
                  <button type="button" className={style.OtherMenuListBtn} onClick={openSkipCancellation}>
                    スキップ取り消し
                  </button>
                </li>
              ) : (
                ''
              )}
              <li>
                <Link
                  to={`/subscriptions/${encodeURIComponent(subscriptionContractId)}/edit`}
                  className={style.OtherMenuListBtn}
                >
                  お届け先変更
                </Link>
              </li>
              <li>
                <button type="button" className={style.OtherMenuListBtn} onClick={onClickShowCouponModal}>
                  クーポンコードを使用
                </button>
              </li>
              <li>
                <button type="button" className={style.OtherMenuListBtn} onClick={openPaymentChange}>
                  お支払い方法変更
                </button>
              </li>
              <li>
                <button type="button" className={style.OtherMenuListBtn} onClick={onClickShowCancellationModal}>
                  ご解約
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* 次回お届けをスキップ モーダル
      -------------------------------------------*/}
      <div className="micromodal" id={`modal-skip-${modalid}`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1}>
          <div role="dialog" className={base.micromodalOverlayDialog} aria-modal="true" aria-labelledby="modal-skip-title">
            <header className={base.ModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-skip-title" className={base.ModalTitle}>
                次回お届けをスキップする
              </h2>
            </header>
            <main id="modal-skip-content" className={base.ModalContent}>
              {/* <p>スキップする回数を選択してください。</p>  <select
                className={`${base.select}`}
                value={skipCount}
                onChange={(e) => {
                  setSkipCount(e.target.value);
                }}
              >
                {skipOptions}
              </select> */}
            </main>
            <footer className={base.ModalFooter}>
              <button className={`${base.sectionBtn} ${base.ModalButton}`} onClick={skip}>
                スキップする
                <span className={base.ModalButtonArrow}>
                  <img src="/image/icon_arrow.svg" alt="" />
                </span>
              </button>
            </footer>
          </div>
        </div>
      </div>
      <div className="micromodal" id={`modal-skip-complete-${modalid}`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1}>
          <div
            role="dialog"
            className={base.micromodalOverlayDialog}
            aria-modal="true"
            aria-labelledby="modal-skip-complete-title"
          >
            <header className={base.ModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-skip-complete-title" className={base.ModalTitle}>
                定期購入のスキップ
              </h2>
            </header>
            <main id="modal-skip-complete-content" className={base.ModalContent}>
              <p>スキップを受け付けました。</p>
            </main>
            <footer className={base.ModalFooter}>
              <button className={base.sectionBtn} data-micromodal-close onClick={onSkipComplete}>
                OK
                <span className={base.ModalButtonArrow}>
                  <img src="/image/icon_arrow.svg" alt="" />
                </span>
              </button>
            </footer>
          </div>
        </div>
      </div>

      {/* お支払い方法変更 モーダル
      -------------------------------------------*/}
      <div className="micromodal" id={`modal-payment-change-${modalid}`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodal-close>
          <div
            role="dialog"
            className={base.micromodalOverlayDialog}
            aria-modal="true"
            aria-labelledby="modal-payment-change-title"
          >
            <header className={base.ModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-coupon-title" className={style.OtherMenuModalTitle}>
                お支払い方法の変更
              </h2>
            </header>
            <main id="modal-payment-change-content" className={base.ModalContent}>
              <p>{subscription.paymentType}変更のご案内メールを送付します。</p>
            </main>
            <footer className={base.ModalFooter}>
              <button className={`${base.sectionBtn} ${base.ModalButton}`} onClick={paymentMethodUpdate}>
                OK
                <span className={base.ModalButtonArrow}>
                  <img src="/image/icon_arrow.svg" alt="" />
                </span>
              </button>
            </footer>
          </div>
        </div>
      </div>

      <div className="micromodal" id={`modal-payment-change-complete-${modalid}`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodal-close>
          <div
            role="dialog"
            className={base.micromodalOverlayDialog}
            aria-modal="true"
            aria-labelledby="modal-payment-change-complete-title"
          >
            <header className={base.ModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-coupon-title" className={style.OtherMenuModalTitle}>
                お支払い方法の変更
              </h2>
            </header>
            <main id="modal-payment-change-content" className={base.ModalContent}>
              <p style={{ fontWeight: 'bold' }}>
                ご登録のメールアドレスに、クレジットカード変更のご案内メールを送付しました。
                <br />
                メールに記載のリンクからクレジットカードの変更をしてください。
              </p>
              <p style={{ background: 'rgb(220 47 47 / 20%)', color: '#DC2F2F', padding: '20px 20px', fontWeight: 'bold' }}>
                お支払い方法の変更リンクの有効期限はメール送信から約8時間となります。
                <br />
                期限が切れた場合はもう一度操作してメールを送信してください。
              </p>
            </main>
            <footer className={base.ModalFooter}>
              <button className={`${base.sectionBtn} ${base.ModalButton}`} data-micromodal-close>
                OK
                <span className={style.OtherModalButtonArrow}>
                  <img src="/image/icon_arrow.svg" alt="" />
                </span>
              </button>
            </footer>
          </div>
        </div>
      </div>

      {/* ご解約モーダル
      -------------------------------------------*/}
      <div className="micromodal" id={`modal-cancellation-${modalid}`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodal-close>
          <div
            role="dialog"
            className={`${base.micromodalOverlayDialog} ${style.OtherMenuModal}`}
            aria-modal="true"
            aria-labelledby="modal-coupon-title"
          >
            <header className={style.OtherMenuModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-coupon-title" className={style.OtherMenuModalTitle}>
                ご解約以外の方法もございます
              </h2>
            </header>
            <main id="modal-coupon-content" className={style.OtherMenuModalContent}>
              <div className={style.otherMenuCancelModalItem}>
                <p>次回お届けをスキップする</p>
                <button className={base.sectionBtn} onClick={openSkipCancel}>
                  スキップする
                  <span className={style.OtherModalButtonArrow}>
                    <img src="/image/icon_arrow.svg" alt="" />
                  </span>
                </button>
              </div>
              <div className={style.otherMenuCancelModalItem}>
                <p>お届け日/周期を変更</p>
                <button className={base.sectionBtn} onClick={openChangeDeliveryDate}>
                  変更する
                  <span className={style.OtherModalButtonArrow}>
                    <img src="/image/icon_arrow.svg" alt="" />
                  </span>
                </button>
              </div>
              <div className={style.otherMenuCancelModalFooter}>
                <Link
                  to={`/subscriptions/${encodeURIComponent(subscriptionContractId)}/cancel`}
                  className={style.otherMenuCancelModalLink}
                >
                  ご解約に進む
                </Link>
              </div>
            </main>
            <div className={style.otherMenuCancelModalItem}>
              <p>※解約ページの表示にはお時間がかかることがございます。</p>
            </div>
          </div>
        </div>
      </div>

      {/* クーポン適用モーダル
      -------------------------------------------*/}
      {/* クーポンコード入力 モーダル */}
      <div className="micromodal" id={`modal-coupon-${modalid}`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodal-close>
          <div
            role="dialog"
            className={`${base.micromodalOverlayDialog} ${style.OtherMenuModal}`}
            aria-modal="true"
            aria-labelledby="modal-coupon-title"
          >
            <header className={style.OtherMenuModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-coupon-title" className={style.OtherMenuModalTitle}>
                クーポンコードを使用
              </h2>
            </header>
            <main id="modal-coupon-content" className={style.OtherMenuModalContent}>
              <div className="">
                <p>クーポンコードを入力する</p>
              </div>
              <input type="text" className={style.OtherMenuInput} onChange={onChangeCouponCode} />
              <button className={base.sectionBtn} onClick={applyCouponCode}>
                適用する
                <span className={style.OtherModalButtonArrow}>
                  <img src="/image/icon_arrow.svg" alt="" />
                </span>
              </button>
            </main>
          </div>
        </div>
      </div>
      {/* クーポン適用完了 モーダル */}
      <div className="micromodal" id={`modal-coupon-complete-${modalid}`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodal-close>
          <div
            role="dialog"
            className={`${base.micromodalOverlayDialog} ${style.OtherMenuModal}`}
            aria-modal="true"
            aria-labelledby="modal-coupon-title"
          >
            <header className={style.OtherMenuModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-coupon-title" className={style.OtherMenuModalTitle}>
                クーポンが適用されました
              </h2>
            </header>
            <main id="modal-coupon-content" className={style.OtherMenuModalContent}>
              <div className="">
                <p>クーポンが適用されました</p>
              </div>
              <button className={base.sectionBtn} data-micromodal-close>
                ＯＫ
                {/* <span className={style.OtherModalButtonArrow}><img src="/image/icon_arrow.svg" alt="" /></span> */}
              </button>
            </main>
          </div>
        </div>
      </div>
      {/* クーポンエラー発生 モーダル */}
      <div className="micromodal" id={`modal-coupon-error-${modalid}`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodxwal-close>
          <div
            role="dialog"
            className={`${base.micromodalOverlayDialog} ${style.OtherMenuModal}`}
            aria-modal="true"
            aria-labelledby="modal-coupon-title"
          >
            <header className={style.OtherMenuModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 className={style.OtherMenuModalTitle}>エラー</h2>
            </header>
            <main className={style.OtherMenuModalContent}>
              <div className="">
                <p>エラーが発生しました。店舗へお問い合わせください。</p>
              </div>
            </main>
            <footer>
              <button
                className={base.sectionBtn}
                onClick={() => {
                  Micromodal.close(`modal-coupon-error-${modalid}`);
                }}
                data-micromodal-close
              >
                閉じる
                {/* <span className={style.OtherModalButtonArrow}><img src="/image/icon_arrow.svg" alt="" /></span> */}
              </button>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
};

export default OtherMenu;
